// @ts-ignore
import React, {createContext, ErrorInfo, MutableRefObject, useEffect, useRef, useState} from "react";
import {toast, ToastContainer} from "react-toastify";
import "./Requests.css";
import {SC_Client} from "./api/SC_Client";
import {SC_Permissions} from "./api/SC_Permissions";
import {SC_User} from "./api/SC_User";
import {SC_Location} from "./api/SC_Location";
import {SC_ServiceInstance} from "./api/SC_ServiceInstance";
import {SC_WorkorderObject} from "./api/SC_WorkorderObject";
import {SC_WorkorderStatus} from "./api/enums/SC_WorkorderStatus";
import GlobalLoadingModal from "./GlobalLoadingModal";
import {ErrorBoundary} from "react-error-boundary";
import {SC_CrewfromAPI} from "./api/crews/CrewUtils";
import {SC_CrewBase} from "./api/crews/SC_CrewBase";
import SimpleModal from "../components/modals/SimpleModal";
import {formatISOishDate} from "./DateUtils";

declare global {
    var ajax_url: string;
    var ajax_nonce: string;
    var myid: number;
}

interface ApiContextValue {
    loading: boolean;
    loadingTime: number;
    loadingPercent?: React.RefObject<number | null>;
    getCoordinates: () => Promise<GeolocationPosition>;
    doAlert: (message: string, error?: boolean) => void;
    doRequest: (formData: FormData, action?: string, showLoading?: boolean, showProgressBar?: boolean) => Promise<any>;
    startLoading: () => void;
    stopLoading: () => void;
    updateCalendarServices: (start: Date, end: Date, statuses?: number[]) => Promise<{
        services: SC_ServiceInstance[];
        workorders: SC_WorkorderObject[];
        locations: SC_Location[];
    }>;
    isMobile: boolean;
    caches?: {
        crews: {value: SC_CrewBase[] | false | null, ref: MutableRefObject<SC_CrewBase[] | false | null>, update: () => Promise<SC_CrewBase[]>};
        users: {value: SC_User[] | false | null, ref: MutableRefObject<SC_User[] | false | null>, update: () => Promise<SC_User[]>};
        employees: {value: SC_User[] | false | null, ref: MutableRefObject<SC_User[] | false | null>, update: () => Promise<SC_User[]>};
        clients: {value: SC_Client[] | false | null, ref: MutableRefObject<SC_Client[] | false | null>, update: () => Promise<SC_Client[]>};
        permissions: {value: SC_Permissions | false | null, ref: MutableRefObject<SC_Permissions | false | null>, update: () => Promise<SC_Permissions>};
    };
    modals: {
        add: (key: string, value: MutableRefObject<any>) => any;
        remove: (key: string) => any;
        current: {id: string, content: MutableRefObject<any>}[];
        ref: MutableRefObject<{id: string, content: MutableRefObject<any>}[]>;
    }
}

const ApiContext = createContext<ApiContextValue>({
    loading: false,
    loadingTime: 0,
    getCoordinates: async () => { throw new Error("Not implemented"); },
    doAlert: () => {},
    doRequest: async () => { throw new Error("Not implemented"); },
    startLoading: () => {},
    stopLoading: () => {},
    isMobile: false,
    modals: {
        add: () => {},
        remove: () => {},
        current: [],
        ref: { current: [] }
    }
});

const useGlobalApiState = (): ApiContextValue => {
    const [loading, setLoading] = useState(false);
    const [loadingTime, setLoadingTime] = useState(0);
    const [activeRequests, setActiveRequests] = useState(0);
    const [permissions, setPermissions] = useState<SC_Permissions | false | null>(null);
    const permissionsRef = useRef(permissions);
    const [crews, setCrews] = useState<SC_CrewBase[] | false | null>(null);
    const crewsRef = useRef(crews);
    const [users, setUsers] = useState<SC_User[] | false | null>(null);
    const usersRef = useRef(users);
    const [clients, setClients] = useState<SC_Client[] | false | null>(null);
    const clientsRef = useRef(clients);
    const [locations, setLocations] = useState<SC_Location[] | false | null>(null);
    const locationsRef = useRef(locations);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const loadingPercent = useRef<number | null>(null);

    // Semaphore to prevent concurrent updateCalendarServices requests
    const updateCalendarLock = useRef<{
        isLocked: boolean;
        promise: Promise<{ services: SC_ServiceInstance[]; workorders: SC_WorkorderObject[]; locations: SC_Location[] } | null>;
    }>({ isLocked: false, promise: null });

    const [activeModals, setActiveModals] = useState<{id: string, content: MutableRefObject<any>}[]>([]);
    const activeModalsRef = useRef<{id: string, content: MutableRefObject<any>}[]>(activeModals);

    const modalRemove = (key: string) => {
        if (!activeModalsRef.current.some(it => it.id === key)) {
            return;
        }

        setActiveModals((modals) => (activeModalsRef.current = modals.filter(it => it.id !== key)));
    };
    const modalAdd = (key: string, modal: React.MutableRefObject<any>) => {
        setActiveModals((modals) => {
            let windex = modals.findIndex(it => it.id === key);

            const newItem = {id: key, content: modal};

            let out: { id: string; content: React.MutableRefObject<any>; }[];

            if (windex === -1) {
                out = [...modals, newItem]
            } else {
                out = [...modals.slice(0, windex), newItem, ...modals.slice(windex + 1, modals.length)];
            }

            return (activeModalsRef.current = out);
        });
    };

    function handleWindowSizeChange(): void {
        setIsMobile(window.innerWidth <= 768);
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);

        // always call this
        getPermissions();

        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const startLoading = (): void => {
        setLoadingTime(Date.now());
        setActiveRequests(prev => {
            const next = prev + 1;
            const load = next > 0;
            setLoading(load);
            if (load)
                loadingPercent.current = null;

            return next;
        });
    };

    const stopLoading = (): void => {
        setTimeout(() => {
            setActiveRequests(prev => {
                const next = prev - 1;
                if (next <= 0) {
                    setLoading(false);
                    return 0;
                }
                return next;
            });
        }, 200);
    };

    const doAlert = (message: string, error = true): void => {
        if (isMobile) {
            let myId = (Math.random() * Number.MAX_SAFE_INTEGER).toString(36);

            let myAlert = <SimpleModal isOpen={true} onClose={() => modalRemove(myId)} title={"A message from Insite"}>
                <span translate={"yes"}>{message}</span>
            </SimpleModal>;

            modalAdd(myId, {current: myAlert});

            // alert(message);
        } else if (error) {
            toast.error(message, {
                autoClose: false,
                hideProgressBar: true,
            });
        } else {
            toast.success(message, {
                autoClose: false,
                hideProgressBar: true,
            });
        }
    };

    const apiCall = async (formData: FormData, action?: string, showLoading = true, showProgressBar = false): Promise<any> => {
        if (showLoading)
            startLoading();

        const params = new URLSearchParams(window.location.search);

        if (params.has("pat"))
            formData.append("pat", params.get("pat")!);

        if (action)
            formData.append("action", action);
        formData.append('nonce', ajax_nonce);

        try {
            let back: any;

            if (showProgressBar) {
                loadingPercent.current = -1;

                back = JSON.parse(await new Promise((resolve, reject) => {
                    const request = new XMLHttpRequest();
                    request.timeout = 0;

                    request.onreadystatechange = function (event: Event): void {
                        if (this.readyState === 4) {
                            if (this.status === 200) {
                                resolve(this.response);
                            } else {
                                reject(this);
                            }
                        }
                    };

                    request.upload.onprogress = (e: ProgressEvent) => {
                        loadingPercent.current = e.loaded / e.total;
                    };

                    request.open("POST", ajax_url, true);
                    request.send(formData);
                }));
            } else {
                const result = await fetch(ajax_url, {
                    method: "POST",
                    body: formData,
                });

                if (!result.ok) {
                    if (result.status === 403) {
                        throw new Error(await result.text());
                    } else if (result.status === 401) {
                        throw new Error(`Authentication error. Refresh the page and try again.`);
                    } else {
                        throw new Error(`Failed to load response (code: ${result.status})`);
                    }
                }

                back = await result.json();
            }

            if (back.code >= 400)
                throw new Error(`A server error occurred (code: ${back.code} reason: ${back.reason})`);

            if (back.newonce)
                ajax_nonce = back.newonce;

            return back;
        } catch (err) {
            if (err.message === "NetworkError when attempting to fetch resource.")
                doAlert("Network Error. Please try to find cell service and try again.");
            else
                doAlert(`API Error: ${err.message || 'Failed to fetch data'}`);

            throw err;
        } finally {
            if (showLoading)
                stopLoading();
        }
    };

    const getPermissions = async (): Promise<SC_Permissions> => {
        return new Promise((resolve, reject) => {
            if (permissions !== false) {
                setPermissions(permissionsRef.current = false);

                apiCall(new FormData(), "get_permissions", true).then(perms => {
                    if (perms.code === 200) {
                        const newPermissions = SC_Permissions.fromAPI(perms.reason);

                        setPermissions(permissionsRef.current = newPermissions);

                        resolve(newPermissions);
                    } else {
                        setPermissions(permissionsRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve(new SC_Permissions());
            }
        });
    };

    const getCrews = async (): Promise<SC_CrewBase[]> => {
        return new Promise((resolve, reject) => {
            if (crews !== false) {
                setCrews(crewsRef.current = false);

                apiCall(new FormData(), "get_crews", true).then(perms => {
                    if (perms.code === 200) {
                        const newCrews = perms.reason.map((o) => SC_CrewfromAPI(o));

                        setCrews(crewsRef.current = newCrews);

                        resolve(newCrews);
                    } else {
                        setCrews(crewsRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve([]);
            }
        });
    };

    const getUsers = async (): Promise<SC_User[]> => {
        return new Promise((resolve, reject) => {
            if (users !== false) {
                setUsers(usersRef.current = false);

                apiCall(new FormData(), "get_users_info", true).then(perms => {
                    if (perms.code === 200) {
                        const newUsers: SC_User[] = perms.reason.map((o) => SC_User.fromAPI(o));

                        newUsers.forEach(user => user.scavengeUsers(newUsers));

                        setUsers(usersRef.current = newUsers);

                        resolve(newUsers);
                    } else {
                        setUsers(usersRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve([]);
            }
        });
    };

    const getEmployees = async (): Promise<SC_User[]> => {
        return new Promise((resolve, reject) => {
            if (users !== false) {
                setUsers(false);

                apiCall(new FormData(), "get_employees", true).then(perms => {
                    if (perms.code === 200) {
                        const newUsers: SC_User[] = perms.reason.map((o) => SC_User.fromAPI(o));

                        newUsers.forEach(user => user.scavengeUsers(newUsers));

                        setUsers(usersRef.current = newUsers);

                        resolve(newUsers);
                    } else {
                        setUsers(usersRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve([]);
            }
        });
    };

    const getClients = async (): Promise<SC_Client[]> => {
        return new Promise((resolve, reject) => {
            if (clients !== false) {
                setClients(clientsRef.current = false);

                apiCall(new FormData(), "get_clients", true).then(perms => {
                    if (perms.code === 200) {
                        const newClients = perms.reason.map(o => SC_Client.fromAPI(o));

                        setClients(clientsRef.current = newClients);

                        resolve(newClients);
                    } else {
                        setClients(clientsRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve([]);
            }
        });
    };

    const getLocations = async (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (locations !== false) {
                setLocations(locationsRef.current = false);

                const formData = new FormData();
                formData.append("size", "100");

                apiCall(formData, "list_locations", true).then(perms => {
                    if (perms.code === 200) {
                        setLocations(locationsRef.current = perms.reason);

                        resolve();
                    } else {
                        setLocations(locationsRef.current = null);

                        reject();
                    }
                }).catch(reject);
            } else {
                resolve();
            }
        });
    };

    const updateCalendarServices = (start: Date, end: Date, statuses: (number)[] = [
        SC_WorkorderStatus.PENDING_CONFIRM,
        SC_WorkorderStatus.PENDING_FINAL_CONFIRM,
        SC_WorkorderStatus.IMMINENT,
        SC_WorkorderStatus.EMERGENCY_IMMINENT,
        SC_WorkorderStatus.IN_PROGRESS,
        SC_WorkorderStatus.IN_PROGRESS_MULTIDAY,
        SC_WorkorderStatus.EMERGENCY_LATE,
        SC_WorkorderStatus.RESCHEDULE,
        SC_WorkorderStatus.PARTIAL_MISSED,
        SC_WorkorderStatus.JOB_COMPLETE,
        SC_WorkorderStatus.READY_TO_BILL,
        SC_WorkorderStatus.INVOICED_REJECTED,
    ]): Promise<{
        services: SC_ServiceInstance[];
        workorders: SC_WorkorderObject[];
        locations: SC_Location[];
    }> => {
        if (updateCalendarLock.current.isLocked)
            throw new Error("Second search detected");

        updateCalendarLock.current.isLocked = true;

        const formData = new FormData();
        formData.append("start", formatISOishDate(start));
        formData.append("end", formatISOishDate(end));
        formData.append("statuses", statuses.join(","));

        return apiCall(formData, "list_services").then(v => {
            const locs: SC_Location[] = v.reason.locations.map(loc => SC_Location.fromAPI(loc, clientsRef.current ?? []));

            const newservs: SC_ServiceInstance[] = Object.values(
                [...v.reason.newservices.map(s => SC_ServiceInstance.fromAPI(s, crewsRef.current))]
                    .reduce((out, curr) => { out[curr.internalID] = curr; return out; }, {} as Record<number, SC_ServiceInstance>)
            );

            const wos: SC_WorkorderObject[] = v.reason.workorders.map(wo => SC_WorkorderObject.fromAPI(wo, locs, usersRef.current, newservs));

            updateCalendarLock.current.isLocked = false;

            return { services: newservs, workorders: wos, locations: locs };
        });
    };

    useEffect(() => {
        if (crews && users) {
            crews.forEach(crew => crew.scavengeUsers(users));
            setCrews(crewsRef.current = crews);

            console.log(crews);
        }
    }, [users, crews]);

    return {
        loading,
        loadingTime,
        loadingPercent,
        getCoordinates: () => {
            return new Promise((resolve, reject) => {
                let wrappedResolve = (v: GeolocationPosition) => { stopLoading(); resolve(v); };
                let wrappedReject = (v: GeolocationPositionError) => { stopLoading(); reject(v); };

                startLoading();

                navigator.geolocation.getCurrentPosition(wrappedResolve, wrappedReject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60_000
                });
            });
        },
        doRequest: apiCall,
        isMobile,
        doAlert,
        startLoading,
        stopLoading,
        updateCalendarServices,
        caches: {
            crews: {value: crews, ref: crewsRef, update: getCrews},
            users: {value: users, ref: usersRef, update: getUsers},
            employees: {value: users, ref: usersRef, update: getEmployees},
            clients: {value: clients, ref: clientsRef, update: getClients},
            permissions: {value: permissions, ref: permissionsRef, update: getPermissions},
        },
        // @ts-ignore
        modals: {
            add: modalAdd,
            remove: modalRemove,
            current: activeModals,
            ref: activeModalsRef
        }
    };
};

type MyReactiveErrorType = {
    error?: any
    filename: string
    lineno: string | number
    colno: string | number
    message: string
};

type ErrorKey = string;

function makeErrorKey(err: MyReactiveErrorType): ErrorKey {
    return `${err.message}:${err.filename}:${err.lineno}:${err.colno}`;
}

// Global API Provider
const ApiProvider = ({ children }: { children: React.ReactNode }) => {
    const globalState = useGlobalApiState();

    let loadingRef = useRef<HTMLDivElement | null>(null);

    // Track reported errors to avoid duplicates
    const reportedErrors = new Set<ErrorKey>();

    function logError(e: MyReactiveErrorType | ErrorEvent, stack?: ErrorEvent | ErrorInfo): void {
        if (stack && !e.error) {
            try {
                const staque = (stack as ErrorInfo).componentStack.trim();
                // @ts-ignore
                e.error = {stack: staque};
                if (!e.filename) {
                    // @ts-ignore
                    const fn = staque.match(/(?<=(?:at )?)\w+(?=@| \()/);
                    if (fn)
                        e.filename = fn[0];
                }
                if (!e.lineno || !e.colno) {
                    // @ts-ignore
                    const rowMatch = staque.match(/(?<=:)\d+(?=:\d+(?:\)?(?:\n|$)))/);
                    if (rowMatch)
                        e.lineno = rowMatch[0];
                    else
                        console.warn("No Row", rowMatch);
                    // @ts-ignore
                    const colMatch = staque.match(/(?<=:\d+:)\d+(?=\)?(?:\n|$))/);
                    if (colMatch)
                        e.colno = colMatch[0];
                    else
                        console.warn("No Col", colMatch);
                }
            } catch (e) {
                console.error("Reporter error", e);
            }
        }

        const errorKey = makeErrorKey(e as MyReactiveErrorType);

        // Skip if we've already reported this exact error
        if (reportedErrors.has(errorKey)) {
            return;
        }

        reportedErrors.add(errorKey);

        (errorQueue.current).push(e as MyReactiveErrorType);
    }

    async function reportError(): Promise<void> {
        if (errorQueue.current.length === 0)
            return;

        uploadLock.current = true;

        try {
            let err = errorQueue.current.shift();

            let formData = new FormData();

            formData.append("errmsg", err.message);
            formData.append("fname", err.filename || "");
            formData.append("line", String(err.lineno || ""));
            formData.append("col", String (err.colno || ""));
            try {
                formData.append("stack", err.error?.stack || "");
            } catch (e) {}
            formData.append("route", window.location.href);

            await globalState.doRequest(formData, "error_reporting", false);

            if ((err.filename || "") === undefined || ((err.filename || "") === "undefined"))
                return;

            if ((err.filename || "") === "_")
                return;

            if (err.message === "ResizeObserver loop completed with undelivered notifications.")
                return;

            toast.warn("An error has occurred. You may need to refresh the page.");

            console.error(err);
        } catch (e) {
            // Silent fail
        } finally {
            uploadLock.current = false;
        }
    }

    /**
     * @type {React.MutableRefObject<MyReactiveErrorType[]>}
     */
    const errorQueue = useRef<MyReactiveErrorType[]>([]);

    const uploadLock = useRef<boolean>(false);

    useEffect(() => {
        window.addEventListener("error", logError);

        let interval = setInterval(() => {
            if (uploadLock.current)
                return;
            reportError();
        }, 2000);

        return () => {
            window.removeEventListener("error", logError);

            clearInterval(interval);
        };
    }, []);

    return (
        <ErrorBoundary
            fallback={<i style={{ display: "inline-block", width: "100%", textAlign: "center" }}>A critical error has occurred</i>}
            onError={logError}
        >
            <ApiContext.Provider value={globalState}>
                {children}
                <GlobalLoadingModal ref={loadingRef} show={globalState.loading} lastTime={globalState.loadingTime} loadingPercent={globalState.loadingPercent} />
                <ToastContainer
                    position="bottom-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
                {
                    globalState.modals.current.map(it => it.content.current)
                }
            </ApiContext.Provider>
        </ErrorBoundary>
    );
};

export { ApiContext, ApiProvider };
